import { Divider, Empty, Flex, Input, Modal, Space, Typography, theme } from 'antd';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { flatMenuItems } from './data/menuItems';
import { getNextSearchActiveIndex, getSearchEnterIndex, shouldHandleSearchNavigation } from './searchModalKeyNav.js';

function isMacPlatform() {
    if (typeof navigator === 'undefined') return false;
    const platform = navigator.userAgentData?.platform || navigator.platform || navigator.userAgent || '';
    return /Mac|iPhone|iPad|iPod/i.test(platform);
}

function groupByLabel(items) {
    const groups = {};
    for (const item of items) {
        const key = item.groupLabel || '通用';
        if (!groups[key]) groups[key] = [];
        groups[key].push(item);
    }
    return groups;
}

export default function SearchModal({ open, onClose, items = flatMenuItems }) {
    const navigate = useNavigate();
    const { token } = theme.useToken();
    const [keyword, setKeyword] = useState('');
    const [activeIndex, setActiveIndex] = useState(-1);
    const openKeys = isMacPlatform() ? ['⌘', 'K'] : ['Ctrl', 'K'];
    const listRef = useRef(null);
    const itemRefs = useRef([]);

    const filtered = useMemo(() => {
        const kw = keyword.trim().toLowerCase();
        if (!kw) return items;
        return items.filter(item => item.label.toLowerCase().includes(kw));
    }, [keyword, items]);

    const grouped = useMemo(() => groupByLabel(filtered), [filtered]);

    // 扁平化列表，用于键盘导航
    const flatList = useMemo(() => {
        const list = [];
        for (const [, groupItems] of Object.entries(grouped)) {
            for (const item of groupItems) {
                list.push(item);
            }
        }
        return list;
    }, [grouped]);

    useEffect(() => {
        if (open) {
            setActiveIndex(-1);
            itemRefs.current = [];
        }
    }, [open]);

    const handleSelect = useCallback(
        key => {
            navigate(key);
            onClose();
            setKeyword('');
        },
        [navigate, onClose],
    );

    const handleClose = useCallback(() => {
        onClose();
        setKeyword('');
    }, [onClose]);

    const handleKeywordChange = useCallback(event => {
        setKeyword(event.target.value);
        setActiveIndex(-1);
        itemRefs.current = [];
    }, []);

    const scrollActiveIntoView = useCallback(index => {
        const el = itemRefs.current[index];
        if (el && listRef.current) {
            el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }
    }, []);

    const handleKeyDown = useCallback(
        e => {
            if (!shouldHandleSearchNavigation(e)) return;

            if (e.key === 'Escape') {
                e.preventDefault();
                handleClose();
                return;
            }

            if (flatList.length === 0) return;

            if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                e.preventDefault();
                setActiveIndex(prev => {
                    const next = getNextSearchActiveIndex(prev, flatList.length, e.key);
                    scrollActiveIntoView(next);
                    return next;
                });
                return;
            }

            if (e.key === 'Enter') {
                const selectedIndex = getSearchEnterIndex(activeIndex, flatList.length);
                if (selectedIndex === -1) return;
                e.preventDefault();
                handleSelect(flatList[selectedIndex].key);
            }
        },
        [flatList, activeIndex, handleSelect, handleClose, scrollActiveIntoView],
    );

    return (
        <Modal open={open} onCancel={handleClose} footer={null} title={null} closable={false} destroyOnHidden width={600} centered styles={{ body: { padding: 0 } }}>
            <div style={{ padding: '12px 0' }} onKeyDown={handleKeyDown}>
                <Input.Search autoFocus allowClear placeholder="搜索后台功能..." size="large" value={keyword} onChange={handleKeywordChange} />
                <Divider style={{ margin: 0 }} />

                <div ref={listRef} style={{ maxHeight: 360, overflowY: 'auto', padding: '8px 0' }}>
                    {filtered.length === 0 ? (
                        <Empty description="暂无匹配功能" image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ padding: '24px 0' }} />
                    ) : (
                        Object.entries(grouped).map(([group, groupItems]) => (
                            <div key={group}>
                                <Typography.Text type="secondary" style={{ display: 'block', padding: '8px 16px 4px' }}>
                                    {group}
                                </Typography.Text>
                                <Flex vertical gap={0}>
                                    {groupItems.map(item => {
                                        const flatIdx = flatList.findIndex(i => i.key === item.key);
                                        const isActive = flatIdx === activeIndex;
                                        return (
                                            <div
                                                key={item.key}
                                                ref={el => {
                                                    if (el) itemRefs.current[flatIdx] = el;
                                                }}
                                                role="button"
                                                tabIndex={0}
                                                aria-selected={isActive}
                                                style={{
                                                    cursor: 'pointer',
                                                    padding: '6px 16px',
                                                    backgroundColor: isActive ? token.colorFillSecondary : 'transparent',
                                                    borderRadius: token.borderRadiusSM,
                                                    transition: 'background-color 0.15s',
                                                }}
                                                onClick={() => handleSelect(item.key)}
                                                onMouseEnter={() => setActiveIndex(flatIdx)}
                                            >
                                                <Space>
                                                    <span className="bi bi-arrow-right-short" aria-hidden />
                                                    {item.label}
                                                </Space>
                                            </div>
                                        );
                                    })}
                                </Flex>
                            </div>
                        ))
                    )}
                </div>
            </div>

            <Divider style={{ margin: 0 }} />

            <Flex justify="flex-end" align="center" gap={16} style={{ padding: '8px 0', color: token.colorTextSecondary, fontSize: 12 }}>
                <Space size={6}>
                    {openKeys.map(key => (
                        <Typography.Text key={key} keyboard>
                            {key}
                        </Typography.Text>
                    ))}
                    <span>打开搜索</span>
                </Space>
                <Space size={6}>
                    <Typography.Text keyboard>↑</Typography.Text>
                    <Typography.Text keyboard>↓</Typography.Text>
                    <span>选择</span>
                </Space>
                <Space size={6}>
                    <Typography.Text keyboard>Enter</Typography.Text>
                    <span>确认</span>
                </Space>
                <Space size={6}>
                    <Typography.Text keyboard>Esc</Typography.Text>
                    <span>关闭</span>
                </Space>
            </Flex>
        </Modal>
    );
}
